import React from 'react';
import { useTranslation } from '../../../../i18n';
import './letter-storm.css';

export interface LetterStormBoardEntry {
  word: string;
  claimCount: number;
  firstPlayerName: string;
  firstPlayerId?: string;
  basePoints: number;
}

export interface LetterStormHostProps {
  letter: string;
  board?: LetterStormBoardEntry[];
}

/**
 * Host display: giant letter + live board of words players have claimed.
 */
export const LetterStormHost: React.FC<LetterStormHostProps> = ({ letter, board = [] }) => {
  const { t } = useTranslation();
  const display = (letter || '?').toUpperCase().slice(0, 1);

  return (
    <div className="letter-storm-host">
      <p className="bonus-host-glow-text--muted letter-storm-host__prompt">
        {t('bonus.host.letterStormPrompt')}
      </p>
      <div
        className="bonus-host-glow-text letter-storm-host__letter"
        aria-label={t('bonus.host.letterStormLetterAria', { letter: display })}
      >
        {display}
      </div>

      <div className="letter-storm-host__board" aria-live="polite" aria-label={t('bonus.host.letterStormBoardAria')}>
        {board.length === 0 ? (
          <p className="letter-storm-host__empty">{t('bonus.host.letterStormBoardEmpty')}</p>
        ) : (
          <div className="letter-storm-host__chips">
            {[...board]
              .sort((a, b) => b.word.length - a.word.length || a.word.localeCompare(b.word))
              .map((entry) => (
                <div
                  key={entry.word}
                  className={
                    entry.claimCount > 1
                      ? 'letter-storm-host__chip letter-storm-host__chip--shared'
                      : 'letter-storm-host__chip'
                  }
                  title={
                    entry.claimCount > 1
                      ? t('bonus.host.letterStormChipShared', {
                          name: entry.firstPlayerName || '—',
                          count: entry.claimCount,
                          points: entry.basePoints,
                        })
                      : t('bonus.host.letterStormChipFirst', {
                          name: entry.firstPlayerName || '—',
                          points: entry.basePoints,
                        })
                  }
                >
                  <span className="letter-storm-host__chip-word">{entry.word}</span>
                  {entry.firstPlayerName ? (
                    <span className="letter-storm-host__chip-meta">{entry.firstPlayerName}</span>
                  ) : null}
                  {entry.claimCount > 1 ? (
                    <span className="letter-storm-host__chip-count">×{entry.claimCount}</span>
                  ) : null}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
